/**
 * Sky furniture: a moon or a sun, two clouds and the stars. Kids only — the
 * plain skin hides it all through `cartoon-only` / `kids-only`.
 *
 * `quiet` keeps the stars but drops the moon and clouds. They are drawn to sit
 * behind the hero card; on a screen with no hero they end up behind running
 * text instead, and pale text over a pale moon is unreadable.
 *
 * Everything is placed against two boxes that the sky must keep out of, because
 * a phone only leaves about eighty pixels of open sky above the hero card and
 * the header sits in it: the wordmark and its label at the reading-start side,
 * and the settings button at the other. In viewBox units those come to about
 * x 19–190 and x 299–371, both from y 19 to y 66, once the sky has been scaled
 * to screens from 360px up — which leaves the sun and moon a hundred-pixel gap
 * between them and the sparkles the strip above. The end-to-end suite measures
 * the real boxes in a real browser and fails if any of the sky lands on one.
 */
export function Sky({ quiet = false }: { quiet?: boolean } = {}) {
  return (
    <>
      <div className="glow" />
      {quiet ? null : (
        <svg
          className="sky-art kids-only"
          width="390"
          height="300"
          viewBox="0 0 390 300"
          preserveAspectRatio="xMidYMin slice"
          aria-hidden="true"
          focusable="false"
        >
          {/* The moon and the sun rise from behind the hero card, right of
              centre, where neither the wordmark nor the settings button is. */}
          <g className="night-only">
            <path
              d="M244 26a30 30 0 1 0 25 47 34 34 0 0 1-25-47Z"
              fill="var(--moon)"
              stroke="var(--outline)"
              strokeWidth="3.4"
              strokeLinejoin="round"
            />
          </g>
          <g className="day-only">
            {/* one ray per element, so a test can tell a ray that strays under
                the settings button from the eight that do not */}
            <g stroke="var(--outline)" strokeWidth="3.4" strokeLinecap="round">
              <path d="M244 41v8" />
              <path d="M244 123v-8" />
              <path d="M203 82h8" />
              <path d="M285 82h-8" />
              <path d="M215 53l5.7 5.7" />
              <path d="M273 111l-5.7-5.7" />
              <path d="M273 53l-5.7 5.7" />
              <path d="M215 111l5.7-5.7" />
            </g>
            <circle
              cx="244"
              cy="82"
              r="27"
              fill="var(--moon)"
              stroke="var(--outline)"
              strokeWidth="3.4"
            />
          </g>
          {/* A cloud at each end, peeking out of the notches the hero card's
              arched top corners leave. Above them there is only the header. */}
          <g fill="var(--cloud)" stroke="var(--outline)" strokeWidth="3.2" strokeLinejoin="round">
            <path
              transform="translate(-64 72)"
              d="M18 34c0-10 8-18 18-18 3-9 11-15 20-15s17 6 20 15c10 0 18 8 18 18s-8 18-18 18H36c-10 0-18-8-18-18Z"
            />
            <path
              transform="translate(105 74)"
              d="M228 20c0-7 6-13 13-13 2-6 8-11 15-11s13 5 15 11c7 0 13 6 13 13s-6 13-13 13h-30c-7 0-13-6-13-13Z"
            />
          </g>
        </svg>
      )}
      <svg
        className="sky-art stars"
        width="390"
        height="330"
        viewBox="0 0 390 330"
        preserveAspectRatio="xMidYMin slice"
        style={{ opacity: 'var(--orn)' }}
        aria-hidden="true"
        focusable="false"
      >
        {/* All of it lives in the strip above the header, or in the gap between
            the header and the hero card — anywhere lower is behind the card. */}
        <g fill="var(--primary)">
          <circle className="twinkle" cx="24" cy="10" r="2.4" />
          <circle className="twinkle b" cx="176" cy="8" r="2" />
          <circle className="twinkle c" cx="206" cy="26" r="2.2" />
          <circle className="twinkle b" cx="330" cy="12" r="1.8" />
        </g>
        <g className="cartoon-only" style={{ filter: 'var(--ink-drop)' }}>
          <path
            className="twinkle d"
            d="M150 1 l2.6 5.2 5.2 2.6 -5.2 2.6 -2.6 5.2 -2.6 -5.2 -5.2 -2.6 5.2 -2.6 Z"
            fill="var(--q3)"
          />
          <path
            className="twinkle e"
            d="M228 4 l2.2 4.4 4.4 2.2 -4.4 2.2 -2.2 4.4 -2.2 -4.4 -4.4 -2.2 4.4 -2.2 Z"
            fill="var(--q1)"
          />
          <path
            className="twinkle c"
            d="M300 3 l2.2 4.4 4.4 2.2 -4.4 2.2 -2.2 4.4 -2.2 -4.4 -4.4 -2.2 4.4 -2.2 Z"
            fill="var(--q4)"
          />
        </g>
      </svg>
    </>
  );
}
